import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';

import Table from 'components/common/Table';
import OOSDailyChart from 'components/stocktaking/OOSDailyChart';
import CreateStocktakingModal from 'components/stocktaking/CreateStocktakingModal';

import { stocktakingApi } from 'api';
import paths from 'routes/paths';

import {
  getStocktakingListColumns,
  STOCKTAKING_FILTER_OPTIONS,
  STOCKTAKING_LIST_TAB,
  STOCKTAKING_STATUS,
} from 'constants/stocktaking';

const StocktakingList = () => {
  const { tenant, warehouse } = useParams();
  const navigate = useNavigate();
  const tableRef = useRef();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [hasActiveStocktaking, setHasActiveStocktaking] = useState(false);

  const columns = useMemo(
    () => getStocktakingListColumns(tenant, warehouse),
    [tenant, warehouse],
  );

  const handleDataChange = useCallback(data => {
    if (Array.isArray(data)) {
      const active = data.some(
        item =>
          item.status === STOCKTAKING_STATUS.IN_PROGRESS ||
          item.status === STOCKTAKING_STATUS.ADJUSTED,
      );
      setHasActiveStocktaking(active);
    }
  }, []);

  const handleCreateSuccess = result => {
    navigate(paths.stocktakings.details(tenant, warehouse, result.id));
  };

  const startButton = hasActiveStocktaking ? (
    <OverlayTrigger
      placement="top"
      overlay={<Tooltip>A stocktaking is already in progress</Tooltip>}
    >
      <span>
        <Button variant="primary" size="sm" disabled>
          Start Stocktaking
        </Button>
      </span>
    </OverlayTrigger>
  ) : (
    <Button
      variant="primary"
      size="sm"
      onClick={() => setShowCreateModal(true)}
    >
      Start Stocktaking
    </Button>
  );

  return (
    <>
      {hasActiveStocktaking && (
        <Alert variant="warning" className="mb-2 py-2 small">
          <strong>⚠ Stocktaking in progress</strong> — inventory operations are
          disabled
        </Alert>
      )}

      <OOSDailyChart />

      <Table
        ref={tableRef}
        title="Stocktaking"
        apiService={stocktakingApi}
        sortable
        columns={columns}
        filterOptions={STOCKTAKING_FILTER_OPTIONS}
        tabOptions={STOCKTAKING_LIST_TAB}
        initialPageSize={25}
        headerActions={startButton}
        onDataChange={handleDataChange}
      />

      <CreateStocktakingModal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
      />
    </>
  );
};

export default StocktakingList;
